import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { startOfDay, subDays, endOfDay } from 'date-fns';
import { Pencil, RotateCcw, Save, X } from 'lucide-react';
import { VIEW_KEY, type DashboardTile } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { useGetViewPreference, useSavePreferenceFor } from '@/hooks/use-user-preferences';
import { DashboardGrid } from '@/components/dashboard/dashboard-grid';
import {
  DashboardDateRangeFilter,
  type PresetLabel,
} from '@/components/dashboard/date-range-filter';
import { TileCatalogDrawer, tileCountsByType } from '@/components/dashboard/tile-catalog-drawer';
import { defaultDashboardLayout, type TileDefinition } from '@/components/dashboard/tile-registry';
import { dashboardSearchSchema } from '@/pages/dashboard/search-schema';

/**
 * Initial date window for new dashboard mounts. Matches the server-side
 * default in `DashboardService.resolvePeriod` so the first render lines
 * up with the data the user expects ("trailing 30 days").
 */
function defaultDateRange(): { from: string; to: string } {
  return {
    from: startOfDay(subDays(new Date(), 30)).toISOString(),
    to: endOfDay(new Date()).toISOString(),
  };
}

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
  validateSearch: dashboardSearchSchema,
});

/**
 * Place a new tile at the bottom of the layout. We don't pack it into
 * gaps — vertical compaction handles cleanup, but stacking new tiles
 * at the end matches user expectations ("the thing I just added is at
 * the bottom") and avoids surprising existing-tile reflows.
 */
function addTileToLayout(layout: DashboardTile[], def: TileDefinition): DashboardTile[] {
  const maxY = layout.reduce((acc, t) => Math.max(acc, t.y + t.h), 0);
  return [
    ...layout,
    {
      id: crypto.randomUUID(),
      type: def.type,
      x: 0,
      y: maxY,
      w: def.defaultSize.w,
      h: def.defaultSize.h,
    },
  ];
}

/**
 * Main dashboard page. Two-mode design:
 *  - View mode (default): tiles are static; "Edit" reveals affordances.
 *  - Edit mode: drag, resize, add via catalog, remove per-tile, save/
 *    cancel/reset-to-defaults. Save commits to user preferences via
 *    PATCH /user-preferences/dashboard.home — atomic at the JSONB
 *    level, so a tab on a different view_key isn't affected.
 *
 * Edit-mode layout is held in local state ("draft"), separate from
 * the saved preference. Cancel discards the draft; save replaces it.
 * The grid renders from the draft when editing, the saved layout
 * when not.
 */
function DashboardPage(): React.ReactElement {
  const { data: savedPref, isLoading: isPrefLoading } = useGetViewPreference(
    VIEW_KEY.DASHBOARD_HOME
  );
  const savePref = useSavePreferenceFor(VIEW_KEY.DASHBOARD_HOME);

  // Date window is in the URL — see `pages/dashboard/search-schema.ts` for
  // why. URL-absent params fall back to the trailing 30-day window so a
  // bare `/dashboard` visit always shows something coherent.
  const search = useSearch({ from: '/_authenticated/dashboard' });
  const navigate = useNavigate();
  const fallback = useMemo(defaultDateRange, []);
  const dateRange = {
    from: search.dateFrom ?? fallback.from,
    to: search.dateTo ?? fallback.to,
  };
  const datePreset = (search.preset as PresetLabel | undefined) ?? 'Last 30 days';

  const setDateRange = (next: { from: string; to: string }): void => {
    navigate({
      to: '/dashboard',
      search: (prev) => ({ ...prev, dateFrom: next.from, dateTo: next.to }),
      replace: true,
    });
  };
  const setDatePreset = (next: PresetLabel): void => {
    navigate({
      to: '/dashboard',
      search: (prev) => ({ ...prev, preset: next }),
      replace: true,
    });
  };

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardTile[] | null>(null);

  // The saved layout — falls back to default for first-time users.
  // Using a ref-stable source keeps the grid from resetting positions
  // on every render of the parent.
  const savedLayout = useMemo<DashboardTile[]>(() => {
    if (savedPref?.tiles && savedPref.tiles.length > 0) return savedPref.tiles;
    return defaultDashboardLayout();
  }, [savedPref]);

  // Seed the draft only when entering edit mode — copying on every
  // render would trample mid-drag positions.
  const enteredEditAt = useRef(false);
  useEffect(() => {
    if (isEditing && !enteredEditAt.current) {
      setDraft(savedLayout.map((t) => ({ ...t })));
      enteredEditAt.current = true;
    } else if (!isEditing && enteredEditAt.current) {
      enteredEditAt.current = false;
    }
  }, [isEditing, savedLayout]);

  const activeLayout = isEditing ? (draft ?? savedLayout) : savedLayout;

  // Each tile self-loads its own metric via `useDashboardMetric` —
  // the dashboard page no longer aggregates a bundled stats request.
  // A slow metric only blocks its own tile, not the rest of the grid.

  const handleSave = async (): Promise<void> => {
    if (!draft) {
      setIsEditing(false);
      return;
    }
    try {
      await savePref.mutateAsync({ tiles: draft });
      setIsEditing(false);
      setDraft(null);
    } catch {
      // Global MutationCache.onError already toasts the failure;
      // keep edit mode open so the user can retry / cancel.
    }
  };

  const handleCancel = (): void => {
    setIsEditing(false);
    setDraft(null);
  };

  const handleReset = (): void => {
    // Reset within edit mode — stage the defaults but DON'T persist
    // until the user clicks Save. That way a misclick is recoverable
    // via Cancel.
    setDraft(defaultDashboardLayout());
  };

  const handleAddTile = (def: TileDefinition): void => {
    setDraft((prev) => {
      const base = prev ?? savedLayout.map((t) => ({ ...t }));
      return addTileToLayout(base, def);
    });
  };

  const handleRemoveTile = (id: string): void => {
    setDraft((prev) => {
      const base = prev ?? savedLayout.map((t) => ({ ...t }));
      return base.filter((t) => t.id !== id);
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b px-6 py-3">
        <div>
          <h1 className="text-lg font-semibold">Dashboard</h1>
          <p className="text-xs text-muted-foreground">
            {isEditing
              ? 'Drag, resize, add or remove tiles. Save when done.'
              : 'Your at-a-glance view. Customize with Edit.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <DashboardDateRangeFilter
              dateFrom={dateRange.from}
              dateTo={dateRange.to}
              onChange={(from, to) => setDateRange({ from, to })}
              preset={datePreset}
              onPresetChange={setDatePreset}
            />
          )}
          {isEditing ? (
            <>
              <TileCatalogDrawer
                onAdd={handleAddTile}
                countByType={tileCountsByType(activeLayout)}
              />
              <Button variant="ghost" size="sm" onClick={handleReset} disabled={savePref.isPending}>
                <RotateCcw className="mr-1 h-4 w-4" />
                Reset to default
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={savePref.isPending}
              >
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={savePref.isPending}>
                <Save className="mr-1 h-4 w-4" />
                {savePref.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="mr-1 h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        {isPrefLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Loading dashboard…
          </div>
        ) : activeLayout.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-sm text-muted-foreground">No tiles on your dashboard yet.</p>
            {!isEditing && (
              <Button size="sm" onClick={() => setIsEditing(true)}>
                <Pencil className="mr-1 h-4 w-4" />
                Customize
              </Button>
            )}
          </div>
        ) : (
          <DashboardGrid
            tiles={activeLayout}
            isEditing={isEditing}
            period={dateRange}
            dashboardCtx={{
              dashFrom: dateRange.from,
              dashTo: dateRange.to,
              dashPreset: datePreset,
            }}
            onLayoutChange={setDraft}
            onRemoveTile={handleRemoveTile}
          />
        )}
      </div>
    </div>
  );
}
